import * as React from 'react';
import { useState, useMemo, useRef } from 'react';
import { entries } from '../../../helpers';
import { useOnClickOutside } from '../../../hooks/useOnClickOutside';
import { Options, SimpleOptions, GroupedOptions } from '../types/Options';
import { PickerItem } from './PickerItem';

interface PickerProps {
    label: string;
    all?: { value: string, label: string };
    selected: string[];
    onChange: (values: any[]) => any;
    withFilter?: boolean;
    options: Options;
};

export const Picker: React.FC<PickerProps> = ({
    label,
    selected,
    onChange,
    all = { value: 'any', label: 'Any' },
    withFilter = false,
    options,
}) => {
    const [focused, setFocused] = useState(false);

    // handles unfocusing the ref
    const ref = useRef<HTMLDivElement>(null);
    useOnClickOutside(ref, () => {
        setFocused(false);
    });

    // Gets all the options as a simple key -> label object
    const flatOptions = useMemo(() => {
        return entries(options).reduce<SimpleOptions>((acc, [key, option]) => {
            if (typeof option === 'string') {
                return {
                    ...acc,
                    [key]: option,
                };
            }

            return {
                ...acc,
                [key]: option.label,
                ...option.children,
            };
        }, {});
    }, [options]);

    const groups = useMemo(() => {
        return entries(options).reduce<GroupedOptions>((acc, [key, option]) => {
            if (typeof option === 'string') return acc;

            return { ...acc, [key]: option };
        }, {});
    }, [options]);

    // converts the given key into a label
    const keyToLabel = (key: string): string => flatOptions[key];

    // Handles field focus
    const handleFocus = (e: React.MouseEvent) => {
        e.preventDefault();

        setFocused(true);
    };

    // checks everything
    const checkAll = (e: React.MouseEvent) => {
        e.preventDefault();

        onChange(Object.keys(options));
    };

    // unchecks everything
    const uncheckAll = (e: React.MouseEvent) => {
        e.preventDefault();

        onChange([]);
    };

    const isSimpleOption = (key: string) => {
        const val = options[key];

        return val && typeof val === 'string';
    };

    const findGroup = (key: string) => {
        return entries(groups).find(([groupKey, group]) => {
            return Object.keys(group.children).includes(key);
        });
    };

    const handleItemClick = (key: string) => {
        if (selected.includes(key)) {
            onChange(selected.filter(k => k !== key));
            return;
        }

        if (isSimpleOption(key)) {
            onChange([...selected, key]);
        } else {
            // if the key is a group child
            if (!options[key]) {
                const [groupKey, group] = findGroup(key);

                const otherChildren = Object.keys(group.children).filter(k => k !== key);
                const allOthersSelected = otherChildren.every(c => selected.includes(c));

                if (selected.includes(groupKey)) {
                    onChange([
                        ...selected.filter(k => k !== groupKey),
                        ...otherChildren,
                    ]);
                } else {
                    if (allOthersSelected) {
                        onChange([
                            ...selected.filter(s => !otherChildren.includes(s)),
                            groupKey,
                        ]);
                    } else {
                        onChange([
                            ...selected,
                            key,
                        ]);
                    }
                }

            // if the key is a group
            } else {
                onChange([...selected, key]);
            }
        }
    };

    const optionCount = useMemo(() => {
        return Object.keys(flatOptions).length;
    }, [flatOptions]);

    // The chips to render
    const chips = useMemo(() => {
        return selected.length < 1 || selected.length === optionCount
            ? [all.label]
            : selected.map(k => keyToLabel(k));
    }, [selected, all, optionCount]);

    // The underlying value
    // const value = useMemo(() => {
    //     return selected.length < 1 || selected.length === optionCount
    //         ? all.value
    //         : selected.join(',');
    // }, [selected, all, optionCount]);

    return (
        <div className={`field picker ${focused ? 'focused' : ''}`} onClick={handleFocus} ref={ref}>
            <p className="label">{label}:</p>
            <div className="chips">
                {chips.map(chip => (
                    <div className="chip" key={chip}>
                        <span>{chip}</span>
                    </div>
                ))}
            </div>

            {focused && (
                <div className="pop-out">
                    {withFilter && (
                        <input type="text" className="filter" placeholder="Filter..." />
                    )}

                    <div className="items">
                        {entries(options).map(([key, option]) => (
                            <PickerItem
                                key={key}
                                id={key}
                                option={option}
                                selected={selected}
                                onClick={handleItemClick}
                            />
                        ))}
                    </div>

                    <div className="footer">
                        <a href="#" onClick={checkAll}>Check All</a>
                        <a href="#" onClick={uncheckAll}>Uncheck All</a>
                    </div>
                </div>
            )}
        </div>
    );
};
